import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import SEO from "../components/seo/SEO";
import CryptoJS from "crypto-js";
import ApiServices from "../../Utils/ApiServices/ApiServices";

/* ================= OTP INPUT ================= */
const OtpInput = ({ otp, setOtp }) => {
  const refs = useRef([]);

  const handleChange = (i, v) => {
    if (!/^[0-9]?$/.test(v)) return;
    const next = [...otp];
    next[i] = v;
    setOtp(next);
    if (v && i < 5) refs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(paste)) return;

    const arr = paste.split("");
    setOtp(arr);

    arr.forEach((digit, index) => {
      if (refs.current[index]) {
        refs.current[index].value = digit;
      }
    });
  };

  return (
    <Box
      sx={{ display: "flex", justifyContent: "center", gap: 1 }}
      onPaste={handlePaste}
    >
      {[...Array(6)].map((_, i) => (
        <TextField
          key={i}
          inputRef={(el) => (refs.current[i] = el)}
          value={otp[i] || ""}
          onChange={(e) => handleChange(i, e.target.value)}
          inputProps={{ maxLength: 1, style: { textAlign: "center" } }}
          sx={{ width: 48 }}
        />
      ))}
    </Box>
  );
};

/* ================= SIGN IN ================= */
export default function SignIn() {
  const navigate = useNavigate();
  const secretKey = process.env.REACT_APP_USER_ENC_DEC_KEY;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [otpDialog, setOtpDialog] = useState(false);
  const [otp, setOtp] = useState([]);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const [encryptedTokens, setEncryptedTokens] = useState({});
  const [accessToken, setAccessToken] = useState("");

  const [resendTimer, setResendTimer] = useState(0);

  /* ================= LOGIN ================= */
  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        user_role: "patient",
        username,
        email: username,
        password,
        client_id: process.env.REACT_APP_CLINTID_ACS_TKN,
        scope: process.env.REACT_APP_PATIENT_SCOPES,
        grant_type: "password",
      };

      const { data, error } = await ApiServices.post(
        "/oauth2/default/token",
        payload,
        { "Content-Type": "application/x-www-form-urlencoded" }
      );

      if (error) throw error;

      await ApiServices.post(
        "/apis/default/api/otpsend",
        { username },
        { "Content-Type": "application/json" }
      );

      setEncryptedTokens({
        userType: CryptoJS.AES.encrypt("Patient", secretKey).toString(),
        access: CryptoJS.AES.encrypt(data.access_token, secretKey).toString(),
        refresh: CryptoJS.AES.encrypt(data.refresh_token, secretKey).toString(),
      });

      setAccessToken(data.access_token);
      setOtpDialog(true);
      setMessage("OTP sent successfully");
      setResendTimer(30);
    } catch {
      setError("Invalid login credentials");
    } finally {
      setLoading(false);
    }
  };

  /* ================= RESEND TIMER ================= */
  useEffect(() => {
    if (resendTimer === 0) return;
    const interval = setInterval(() => {
      setResendTimer((t) => t - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  /* ================= OTP AUTO SUBMIT ================= */
  useEffect(() => {
    if (otp.length === 6 && otp.every(Boolean)) verifyOtp();
  }, [otp]);

  const verifyOtp = async () => {
    try {
      const res = await ApiServices.post(
        "/apis/default/api/verifyotpsent",
        { username, otp: otp.join("") },
        { "Content-Type": "application/json" }
      );

      if (!res.data.data.status) throw new Error();

      sessionStorage.setItem("LoginUserType", encryptedTokens.userType);
      sessionStorage.setItem("LoginUserAccessToken", encryptedTokens.access);
      sessionStorage.setItem("LoginUserRefreshToken", encryptedTokens.refresh);

      const userRes = await ApiServices.get(
        "/apis/default/portal/patient",
        { Authorization: `Bearer ${accessToken}` }
      );

      sessionStorage.setItem(
        "LoginUserDetails",
        CryptoJS.AES.encrypt(
          JSON.stringify(userRes.data.data),
          secretKey
        ).toString()
      );

      setMessage("Login successful");
      setOtpDialog(false);
      setTimeout(() => navigate("/patient/dashboard"), 1500);
    } catch {
      setError("OTP verification failed");
      setOtp([]);
    }
  };

  return (
    <>
      <SEO title="Sign In" description="Sign in to your Rite Wellness account" canonical="/signin" noIndex />

      <Box component="main" className="wellness-bg" sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", py: 12 }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ width: "100%", maxWidth: 420 }}>
          <Box className="glass-card" sx={{ p: 4 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box component={Link} to="/" sx={{ display: "inline-flex", alignItems: "center", gap: 1, textDecoration: "none" }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: "primary.main", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FavoriteRoundedIcon sx={{ color: "#fff" }} />
                </Box>
                <Typography fontWeight={800} style={{ color: "#000000" }}>Rite <span style={{ color: "#27A599" }}>Wellness</span></Typography>
              </Box>
              <Typography sx={{ mt: 2, fontWeight: 800, fontSize: 22 }}>Welcome Back</Typography>
            </Box>

            <Box component="form" onSubmit={handleLogin} sx={{ display: "grid", gap: 2 }}>
              <TextField value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Email Address" fullWidth />
              <TextField
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(v => !v)}>
                        {showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={22} /> : "Sign In"}
              </Button>
            </Box>

            {/* EXTRA LINKS */}
            <Box sx={{ mt: 2, textAlign: "center", fontSize: 14 }}>
              
                <Box
                              component={Link}
                              to="/forgot-password"
                              sx={{
                                color: "primary.main",
                                fontWeight: 700,
                                textDecoration: "none",
                              }}
                            >
                             Forgot password?
                            </Box>
              <br />
             
                <Box
                              component={Link}
                              to="/provider-signin"
                              sx={{
                                color: "primary.main",
                                fontWeight: 700,
                                textDecoration: "none",
                              }}
                            >
                           Doctor Login
                            </Box>
            </Box>

            <Typography sx={{ mt: 2, textAlign: "center", fontSize: 14 }}>
              Don’t have an account? 
              <Box
                              component={Link}
                              to="/signup"
                              sx={{
                                color: "primary.main",
                                fontWeight: 700,
                                textDecoration: "none",
                              }}
                            >
                             Sign Up
                            </Box>
              
            </Typography>
          </Box>
        </motion.div>
      </Box>

      {/* OTP DIALOG */}
      <Dialog open={otpDialog}>
        <DialogTitle>Enter OTP</DialogTitle>
        <DialogContent>
          <OtpInput otp={otp} setOtp={setOtp} />
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Button
              size="small"
              disabled={resendTimer > 0}
              onClick={handleLogin}
            >
              {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar open={!!error || !!message} autoHideDuration={4000} onClose={() => { setError(null); setMessage(null); }}>
        <MuiAlert severity={error ? "error" : "success"} variant="filled">
          {error || message}
        </MuiAlert>
      </Snackbar>
    </>
  );
}
